import { useLingui } from "@lingui/react/macro";
import { scopedThreadKey, scopeThreadRef } from "@t3tools/client-runtime";
import { CloudIcon, LockKeyholeIcon } from "lucide-react";
import { usePrimaryEnvironmentId } from "../environments/primary";
import {
  useSavedEnvironmentRegistryStore,
  useSavedEnvironmentRuntimeStore,
} from "../environments/runtime";
import { useUiStateStore } from "../uiStateStore";
import {
  resolveThreadStatusPill,
  THREAD_STATUS_PILL_LABELS,
  type ThreadStatusPill,
} from "./Sidebar.logic";
import type { SidebarThreadSummary } from "../types";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

export function ThreadStatusLabel({
  status,
  compact = false,
}: {
  status: ThreadStatusPill;
  compact?: boolean;
}) {
  const { i18n } = useLingui();
  const statusLabel = i18n._(THREAD_STATUS_PILL_LABELS[status.label]);
  if (compact) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              aria-label={statusLabel}
              className={`inline-flex size-3.5 shrink-0 items-center justify-center ${status.colorClass}`}
            />
          }
        >
          <span
            className={`size-[9px] rounded-full ${status.dotClass} ${
              status.pulse ? "animate-pulse" : ""
            }`}
          />
        </TooltipTrigger>
        <TooltipPopup side="top">{statusLabel}</TooltipPopup>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            aria-label={statusLabel}
            className={`inline-flex items-center gap-1 text-[10px] ${status.colorClass}`}
          />
        }
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${status.dotClass} ${
            status.pulse ? "animate-pulse" : ""
          }`}
        />
        <span className="hidden md:inline">{statusLabel}</span>
      </TooltipTrigger>
      <TooltipPopup side="top">{statusLabel}</TooltipPopup>
    </Tooltip>
  );
}

export function ThreadRowLeadingStatus({ thread }: { thread: SidebarThreadSummary }) {
  const { t } = useLingui();
  const threadRef = scopeThreadRef(thread.environmentId, thread.id);
  const lastVisitedAt = useUiStateStore(
    (state) => state.threadLastVisitedAtById[scopedThreadKey(threadRef)],
  );
  const threadStatus = resolveThreadStatusPill({
    thread: {
      ...thread,
      lastVisitedAt,
    },
  });

  if (thread.block) {
    const label = t`Chat blocked`;
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              role="img"
              aria-label={`${label}: ${thread.block.title}`}
              className="inline-flex size-3.5 shrink-0 items-center justify-center text-warning-foreground"
            />
          }
        >
          <LockKeyholeIcon className="size-3.5" aria-hidden="true" />
        </TooltipTrigger>
        <TooltipPopup side="top" className="max-w-80 whitespace-normal">
          <div className="space-y-1">
            <p>
              {label}: {thread.block.title}
            </p>
            <p>{thread.block.text}</p>
          </div>
        </TooltipPopup>
      </Tooltip>
    );
  }

  if (!threadStatus) {
    return null;
  }

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5">
      <ThreadStatusLabel status={threadStatus} />
    </span>
  );
}

/**
 * Non-interactive trailing status icons for a thread row in compact contexts
 * like the command palette. Shows a remote environment indicator, matching the
 * sidebar's trailing indicators.
 */
export function ThreadRowTrailingStatus({ thread }: { thread: SidebarThreadSummary }) {
  const { t } = useLingui();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const isRemoteThread =
    primaryEnvironmentId !== null && thread.environmentId !== primaryEnvironmentId;
  const remoteEnvLabel = useSavedEnvironmentRuntimeStore(
    (state) => state.byId[thread.environmentId]?.descriptor?.label ?? null,
  );
  const remoteEnvSavedLabel = useSavedEnvironmentRegistryStore(
    (state) => state.byId[thread.environmentId]?.label ?? null,
  );
  const threadEnvironmentLabel = isRemoteThread
    ? (remoteEnvLabel ?? remoteEnvSavedLabel ?? t`Remote`)
    : null;
  if (!isRemoteThread) {
    return null;
  }

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5">
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              aria-label={threadEnvironmentLabel ?? t`Remote`}
              className="inline-flex items-center justify-center"
            />
          }
        >
          <CloudIcon className="size-3 text-muted-foreground/60" />
        </TooltipTrigger>
        <TooltipPopup side="top">{threadEnvironmentLabel}</TooltipPopup>
      </Tooltip>
    </span>
  );
}
